/**
 * Kontrakt zápisu ručního žebříčku zájezdů (řez 08, vize D6) — sdílený mezi administrací,
 * která payload staví, a rozhraním `PATCH /api/admin/trips/reorder`, které ho validuje
 * a zapisuje.
 *
 * Bydlí v `packages/shared` ze dvou důvodů: je to obousměrný kontrakt (tvar položky zná
 * odesílatel i příjemce) a administrace nemá vlastní testovací runtime — pravidla, která
 * se jí týkají, proto žijí tady, stejně jako `adminLocaleGuard` (viz hlavička
 * `vitest.config.ts`). Bez toho by tahle půlka řezu neměla jak být pokrytá testem.
 */

import { compareCzechStrict } from "./utils/czechSort";

/** Jedna položka zápisu: pozice v žebříčku, nebo `null` = vyřazení (běžný stav, ne chyba). */
export interface TripReorderItem {
  id: string;
  sortOrder: number | null;
}

/**
 * Minimum, které stačí k odvození žebříčku z odpovědi serveru. `sortOrder` je nepovinný,
 * protože slimové projekce zájezdu ho vracet nemusí — „nezařazený" a „nevím" se tu chovají
 * stejně (`!= null`), a to je záměr: bez pozice zájezd do žebříčku nepatří.
 */
export interface RankedTripRow {
  id: string;
  sortOrder?: number | null;
}

/**
 * Žebříček tak, jak ho vidí server: zařazené zájezdy vzestupně podle `sortOrder`,
 * nezařazené (`null`) vůbec. Pořadí je jediné, co stránka „Pořadí zájezdů" ze seznamu
 * potřebuje — zbytek karty se dohledá podle id.
 */
export function rankedTripIds(trips: readonly RankedTripRow[]): string[] {
  return trips
    .filter((trip) => trip.sortOrder != null)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((trip) => trip.id);
}

/**
 * Payload zápisu = CELÝ nový žebříček přečíslovaný od 0 **plus** `sortOrder: null` pro
 * každý zájezd, který server dosud za zařazený považuje, ale v novém žebříčku není.
 * Přečíslování dělá klient, aby v žebříčku po odebrání nezůstala díra (AK13).
 *
 * Vyřazené položky se DOPOČÍTÁVAJÍ z posledního známého stavu serveru — nedrží se ve
 * stavu stránky. Kdyby se držely, ztratil by je první neúspěšný zápis: další (už úspěšné)
 * uložení by `null` neposlalo, refetch by zájezd načetl s původním `sortOrder` a vrátil
 * ho zpátky do žebříčku — přesně ta „duchová pozice", kterou vynulování řeší. Takhle se
 * vyřazení opakuje v každém dalším pokusu, dokud ho server nepotvrdí.
 */
export function buildTripReorderItems(
  rankedIds: readonly string[],
  serverRankedIds: readonly string[],
): TripReorderItem[] {
  const ranked = new Set(rankedIds);
  return [
    ...rankedIds.map((id, index) => ({ id, sortOrder: index as number | null })),
    ...serverRankedIds.filter((id) => !ranked.has(id)).map((id) => ({ id, sortOrder: null })),
  ];
}

/**
 * Shoduje se žebříček ze serveru s rozpracovaným? Rozpracovaný žebříček se kreslí tak
 * dlouho, dokud ho server nepotvrdí TÍMHLE porovnáním — ne „po úspěšném PATCHi".
 * Odpověď na PATCH totiž přijde dřív než refetch, takže mezi nimi je okno, ve kterém
 * server data ještě nesou staré pořadí; kdyby se rozpracovaný stav zahodil hned,
 * žebříček by v tom okně viditelně poskočil zpátky.
 */
export function sameRanking(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** Minimum, které stačí k seřazení NEZAŘAZENÝCH zájezdů (viz `compareUnrankedTrips`). */
export interface UnrankedTripRow {
  /**
   * Kdy zájezd vznikl. `Date` je tu kvůli typu `Trip.createdAt` v `types/index.ts`;
   * přes JSON i z D1 chodí ve skutečnosti VŽDY řetězec, takže převod níž je pojistka,
   * ne větev, se kterou se běžně počítá. `null`/chybějící hodnotu snese ze stejného
   * důvodu jako `RankedTripRow.sortOrder`: slimová projekce zájezdu ji vracet nemusí
   * a řazení kvůli tomu nesmí spadnout — takový zájezd jde na konec.
   */
  createdAt: string | Date | null | undefined;
  title: string;
}

const createdAtKey = (row: UnrankedTripRow): string => {
  if (!row.createdAt) return "";
  return typeof row.createdAt === "string" ? row.createdAt : row.createdAt.toISOString();
};

/**
 * Pořadí NEZAŘAZENÝCH zájezdů (vize struktura-webu D6/D8): od nejnovějšího, remízu
 * rozhoduje název.
 *
 * Bydlí ve sdíleném balíčku, protože totéž pravidlo potřebují DVA konzumenti a rozejít
 * se nesmí: `compareByRank` v API (výchozí pořadí katalogu) a panel „Nezařazené
 * publikované zájezdy" na stránce „Pořadí zájezdů" v administraci, jejíž popisek slibuje
 * „vypisuje se pod žebříčkem, od nejnovějšího". Do téhle opravy administrace neřadila
 * vůbec — operátor tam viděl jiné pořadí než zákazník na webu, a to zrovna na stránce,
 * která má pořadí řídit. Administrace navíc nemá vlastní testovací runtime, takže
 * pravidlo je testovatelné jen odsud (viz hlavička souboru).
 *
 * `createdAt` se porovnává LEXIKOGRAFICKY jako řetězec — nikde se nekonstruuje `Date`,
 * stejně jako u `sortBy=departure`. Produkce má dva tvary (`2026-07-29T19:19:59.242Z`
 * i `2026-01-25 16:34:30`), které se liší až na 11. znaku; žádný jediný den obě varianty
 * nemíchá, takže je porovnání přesto správné. Neopravovat migrací naslepo.
 *
 * Tiebreak podle názvu MUSÍ zůstat: bez něj rozhoduje pořadí řádků z D1 a zájezd se může
 * objevit na první i druhé stránce zároveň (nebo na žádné). Proto `compareCzechStrict`.
 */
export function compareUnrankedTrips(a: UnrankedTripRow, b: UnrankedTripRow): number {
  const aKey = createdAtKey(a);
  const bKey = createdAtKey(b);
  if (aKey !== bKey) return aKey > bKey ? -1 : 1;
  return compareCzechStrict(a.title, b.title);
}
